// V2 版式：dumbbell（前端 DOM 版）——对照后端 _shared/pptx/v2/layouts/dumbbell.ts 逐坐标移植
// 3–6 行前后对比记分板；每行自有刻度（lo/hi，不共享轴）——轨道 + 前后两圆点 + 连线；
// 数值标签方向感知、永远放在哑铃跨度外侧（左向行镜像，任何跨度下不撞字）；
// 退步行（after<before）染风险色（semanticPair.bad，色彩之外数值与符号双信号）；
// hero 行左侧 accent 条 + 加粗行名。几何取 V2GEO["dumbbell"]（deno dump 同源，勿手改数值）。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import { semanticPair } from "./semantic";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

// deno dump 同源：与后端 dumbbell.ts GEO 一一对应
const GEO = V2GEO["dumbbell"] as any;

// dumbbell block 结构以后端 _shared/pptx/types.ts 为准（slides.ts Block 已同步）
type DumbbellItem = { name: string; before: number; after: number; lo: number; hi: number; unit?: string };
type DumbbellBlock = { type: "dumbbell"; items: DumbbellItem[]; hero?: number };

export default function V2Dumbbell({ c, skin, page, total, editable, handlers }: V2LayoutProps) {
  const m = SPACING.marginX;
  const W = 13.333;
  const sem = semanticPair(skin);

  const di = c.blocks.findIndex((b) => (b as { type: string }).type === "dumbbell");
  const db = di >= 0 ? (c.blocks[di] as unknown as DumbbellBlock) : null;
  const items = db ? db.items.slice(0, 6) : [];
  const n = items.length;
  const hero = db?.hero ?? -1;

  const plotH = GEO.chart.bottom - GEO.chart.top;
  const rowH = n > 0 ? (plotH - (n - 1) * GEO.rowGap) / n : plotH;
  const trackX = m + GEO.nameW + GEO.nameGap + GEO.labelW;
  const trackW = W - m - trackX - GEO.labelW;

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "dumbbell")} page={page} total={total} foot={footOf()} />

      {/* 页面标题（长度分档） */}
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(GEO.title.x), top: px(GEO.title.y),
          width: px(GEO.title.w), height: px(GEO.title.h),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: skin.serifHead ? 700 : 400, lineHeight: 1.15,
        }}
      />

      {items.map((it, i) => {
        const y = GEO.chart.top + i * (rowH + GEO.rowGap);
        const cy = y + rowH / 2;
        const isHero = i === hero;
        const regress = it.after < it.before;
        // 每行自有刻度（lo/hi，不共享轴）；前端容错：lo≥hi 时按 1 跨度（后端数据合法时不受影响）
        const span = Math.max(it.hi - it.lo, 1e-9);
        const xOf = (v: number) => trackX + ((v - it.lo) / span) * trackW;
        const xBefore = xOf(it.before);
        const xAfter = xOf(it.after);
        const linkColor = regress ? sem.bad : skin.accent;
        const fmt = (v: number) => `${v.toLocaleString("en-US")}${it.unit ?? ""}`;
        return (
          <div key={i}>
            {/* hero 左侧竖 accent 条 */}
            {isHero && (
              <div style={{
                position: "absolute", left: px(m), top: px(y),
                width: px(GEO.heroBarW), height: px(rowH), background: skin.accent,
              }} />
            )}
            {/* 行名（hero 加粗） */}
            <EditableText
              value={it.name} path={`blocks.${di}.items.${i}.name`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(m + (isHero ? GEO.heroBarW + 0.1 : 0)), top: px(y),
                width: px(GEO.nameW), height: px(rowH),
                fontSize: pt(skin.scale.body), color: skin.text, fontFamily: skin.fontBody,
                fontWeight: isHero ? 700 : 400,
                display: "flex", alignItems: "center", whiteSpace: "nowrap",
              }}
            />
            {/* 轨道（hairline 细线） */}
            <div style={{
              position: "absolute", left: px(trackX), top: px(cy),
              width: px(trackW), height: 1, background: skin.hairline,
            }} />
            {/* 连线（before→after；退步行染风险色） */}
            <div style={{
              position: "absolute", left: px(Math.min(xBefore, xAfter)), top: px(cy),
              width: px(Math.abs(xAfter - xBefore)), height: 1, background: linkColor,
            }} />
            {/* 两圆点 */}
            {[xBefore, xAfter].map((x, j) => (
              <div key={j} style={{
                position: "absolute", left: px(x - GEO.dotD / 2), top: px(cy - GEO.dotD / 2),
                width: px(GEO.dotD), height: px(GEO.dotD),
                background: linkColor, borderRadius: "50%",
              }} />
            ))}
            {/* 数值标签方向感知：永远在跨度外侧（左向行镜像，不撞字） */}
            <EditableText
              value={fmt(it.before)} path={`blocks.${di}.items.${i}.before`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(xBefore - GEO.labelGap - GEO.labelW), top: px(y),
                width: px(GEO.labelW), height: px(rowH),
                fontSize: pt(skin.scale.meta), color: regress ? sem.bad : skin.sub, fontFamily: skin.fontMono,
                textAlign: "right", display: "flex", alignItems: "center", justifyContent: "flex-end",
              }}
            />
            <EditableText
              value={fmt(it.after)} path={`blocks.${di}.items.${i}.after`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(xAfter + GEO.labelGap), top: px(y),
                width: px(GEO.labelW), height: px(rowH),
                fontSize: pt(skin.scale.meta), color: regress ? sem.bad : skin.accent, fontFamily: skin.fontMono,
                fontWeight: 700, textAlign: "left",
                display: "flex", alignItems: "center", justifyContent: "flex-start",
              }}
            />
          </div>
        );
      })}
    </>
  );
}
